import * as THREE from "three";
import { buildIkChainFromScene } from "../ik/chainBuilder";
import { computeKinematics } from "../ik/forwardKinematics";
import { solvePositionIk } from "../ik/solver";
import { findGripperTargetOffset } from "../ik/ikTarget/gripperTargetOffset";
import {
  buildKeepDownOrientationTargetZFrame,
  rankGraspOrientationsWorldZUp,
  quatToMj,
  resolveKeepDownLocalAxes,
} from "../ik/orientationTargets";
import { getSceneState, getSceneActions } from "../stores/sceneStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { resolveTopmostAncestorBodyUuid } from "./resolveTopmostBody";
import { logger } from "../utils/logger";
import type { BodyUUID } from "../types/scene";
import type { IkOrientationMode } from "./types";
import { getGraspState } from "../stores/graspStore";
import { resolveRuntimeObject } from "./actions/helpers";

const warnedAlignWithGraspFallback = new Set<string>();
type BodyWithNameAndUuid = { name?: string; uuid?: string };

/** One-shot position IK in scene Y-up frame; updates joint refs via sceneActions. */
export function applyWorldPositionIk(args: {
  endEffectorUuid: BodyUUID;
  worldXyz: [number, number, number];
  useGripperTargetSite?: boolean;
  orientationMode?: IkOrientationMode;
  orientationStrength?: number;
  orientationTargetBodyUuid?: string;
}): boolean {
  const {
    endEffectorUuid,
    worldXyz,
    useGripperTargetSite = false,
    orientationMode = "free",
    orientationStrength = 0.8,
    orientationTargetBodyUuid = "",
  } = args;
  const scene = getSceneState();
  const chainRoot = resolveTopmostAncestorBodyUuid(scene, endEffectorUuid);
  const chain = buildIkChainFromScene(scene, endEffectorUuid, chainRoot || undefined);
  if (!chain) return false;

  const yToZ = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
  const targetOffset = useGripperTargetSite
    ? findGripperTargetOffset(scene, endEffectorUuid, yToZ, true)
    : null;
  if (targetOffset) {
    chain.endEffectorOffset = [
      targetOffset.offsetZFrame.x,
      targetOffset.offsetZFrame.y,
      targetOffset.offsetZFrame.z,
    ];
  }

  const dofLinks = chain.links
    .filter(
      (l) => (l.kind === "hinge" || l.kind === "slide") && l.jointUuid && l.dofIndex !== undefined
    )
    .sort((a, b) => (a.dofIndex ?? 0) - (b.dofIndex ?? 0));

  const sceneSeed = dofLinks.map((link) => {
    const j = link.jointUuid ? scene.joints?.[link.jointUuid] : null;
    const ref = j && "ref" in j && typeof j.ref === "number" ? j.ref : 0;
    return Number.isFinite(ref) ? ref : 0;
  });

  const sceneTarget = new THREE.Vector3(worldXyz[0], worldXyz[1], worldXyz[2]);
  const isPhysicsRunning = usePhysicsStore.getState().isRunning === true;
  const minSceneY = 0.02;
  const zToY = yToZ.clone().invert();
  const zFrameTarget = sceneTarget.clone().applyQuaternion(yToZ);
  void zFrameTarget;

  const seeds: number[][] = [sceneSeed];
  try {
    const w = globalThis as unknown as { __ikLastSolution?: number[] };
    if (Array.isArray(w.__ikLastSolution) && w.__ikLastSolution.length === sceneSeed.length) {
      seeds.unshift(w.__ikLastSolution.slice());
    }
  } catch {
    /* ignore */
  }
  const floorExemptBodyUuids = new Set<BodyUUID>();
  Object.values(scene.bodies || {}).forEach((body: BodyWithNameAndUuid) => {
    const name = (body?.name || "").toLowerCase();
    if (name.includes("pad")) floorExemptBodyUuids.add(body.uuid as BodyUUID);
  });

  const evaluatePath = (
    fromJoints: number[],
    toJoints: number[],
    targetSceneForPath: THREE.Vector3,
    approachMinY: number,
    approachTailSteps: number
  ): { safe: boolean; approachPenalty: number } => {
    const steps = 12;
    const allowEeBelowFloor = targetSceneForPath.y < minSceneY;
    let approachViolations = 0;
    for (let step = 0; step <= steps; step += 1) {
      const t = step / steps;
      const blended = toJoints.map((targetVal, idx) => {
        const startVal = fromJoints[idx] ?? 0;
        return startVal + (targetVal - startVal) * t;
      });
      const stepFk = computeKinematics(chain, blended);
      for (const [linkIdx, linkState] of stepFk.linkStates.entries()) {
        const linkScene = linkState.position.clone().applyQuaternion(zToY);
        if (step > 0 && linkScene.y < minSceneY) {
          const linkBodyUuid = chain.links[linkIdx]?.bodyUuid;
          const padAllowance = 0.01;
          const padMinY = minSceneY - padAllowance;
          if (!linkBodyUuid || !floorExemptBodyUuids.has(linkBodyUuid as BodyUUID)) {
            return { safe: false, approachPenalty: 1 };
          }
          if (linkScene.y < padMinY) {
            return { safe: false, approachPenalty: 1 };
          }
        }
      }
      const eeSceneStep = stepFk.endEffector.position.clone().applyQuaternion(zToY);
      const isFinalApproach = step >= Math.max(steps - approachTailSteps, 0);
      if (!isFinalApproach && step > 0 && eeSceneStep.y < approachMinY) {
        approachViolations += 1;
      }
      if (step > 0 && eeSceneStep.y < minSceneY && !(allowEeBelowFloor && step === steps)) {
        return { safe: false, approachPenalty: 1 };
      }
    }
    const approachPenalty = steps > 0 ? approachViolations / steps : 0;
    return { safe: true, approachPenalty };
  };

  // Behavior nodes should respect the exact authored target (especially when paired
  // with explicit gates like "EE hovering above pick"), so do not silently swap in
  // lifted fallback targets here.
  const sceneTargets = [sceneTarget.clone()];
  const approachMinY = 0.035;
  const approachTailSteps = 3;
  const { forwardLocal: gripperForwardLocal, upLocal: gripperUpLocal } =
    resolveKeepDownLocalAxes(targetOffset);

  let pick: ReturnType<typeof solvePositionIk> | null = null;
  let chosenScore: number | null = null;
  for (const candidate of sceneTargets) {
    const candidateZ = candidate.clone().applyQuaternion(yToZ);
    for (const seed of seeds) {
      const fkSeed = computeKinematics(chain, seed);
      const eeWorldPosScene = fkSeed.endEffector.position.clone().applyQuaternion(zToY);
      const graspOrientationTarget = (() => {
        if (orientationMode !== "alignWithGrasp" || !orientationTargetBodyUuid) return null;
        const grasps = getGraspState().graspsByBodyUuid[orientationTargetBodyUuid] || [];
        if (!Array.isArray(grasps) || grasps.length === 0) return null;
        const targetObj = resolveRuntimeObject(orientationTargetBodyUuid);
        if (!targetObj) return null;
        const bodyWorldPosScene = new THREE.Vector3();
        const bodyWorldQuatScene = new THREE.Quaternion();
        targetObj.getWorldPosition(bodyWorldPosScene);
        targetObj.getWorldQuaternion(bodyWorldQuatScene);
        const ranked = rankGraspOrientationsWorldZUp({
          grasps,
          bodyWorldPosScene,
          bodyWorldQuatScene,
          yToZ,
          eeWorldPosScene,
        });
        return ranked[0]?.orientationZ || null;
      })();
      const orientationTarget =
        orientationMode === "alignWithGrasp"
          ? graspOrientationTarget ||
            buildKeepDownOrientationTargetZFrame({
              currentRotationZ: fkSeed.endEffector.rotation.clone(),
              forwardLocal: gripperForwardLocal,
              upLocal: gripperUpLocal,
              forwardWorldZ: new THREE.Vector3(0, 0, -1),
              worldUpZ: new THREE.Vector3(0, 1, 0),
              symmetryTurns: 4,
            })
          : orientationMode === "keepDown"
            ? buildKeepDownOrientationTargetZFrame({
                currentRotationZ: fkSeed.endEffector.rotation.clone(),
                forwardLocal: gripperForwardLocal,
                upLocal: gripperUpLocal,
                forwardWorldZ: new THREE.Vector3(0, 0, -1),
                worldUpZ: new THREE.Vector3(0, 1, 0),
                symmetryTurns: 4,
              })
            : null;
      if (orientationMode === "alignWithGrasp" && !graspOrientationTarget) {
        const warnKey = `${orientationTargetBodyUuid || "none"}:${endEffectorUuid}`;
        if (!warnedAlignWithGraspFallback.has(warnKey)) {
          warnedAlignWithGraspFallback.add(warnKey);
          logger.warn(
            "[behavior/ik] alignWithGrasp requested but grasp data/runtime target unavailable; falling back to keepDown",
            {
              orientationTargetBodyUuid: orientationTargetBodyUuid || null,
              endEffectorUuid,
            }
          );
        }
      }
      const attempt = solvePositionIk(
        chain,
        orientationTarget
          ? {
              position: [candidateZ.x, candidateZ.y, candidateZ.z],
              orientation: quatToMj(orientationTarget),
            }
          : { position: [candidateZ.x, candidateZ.y, candidateZ.z] },
        seed,
        {
          maxIterations: 192,
          tolerance: 1e-5,
          rotationTolerance: orientationTarget ? 0.12 : undefined,
          damping: 0.01,
          stepSize: 0.75,
          orientationWeight: orientationTarget
            ? Math.max(0, Math.min(2, Number(orientationStrength) || 0.8))
            : undefined,
        }
      );
      if (!attempt || attempt.error > 0.05 || attempt.status !== "success") continue;
      const pathEval = evaluatePath(
        seed,
        attempt.jointValues,
        candidate,
        approachMinY,
        approachTailSteps
      );
      if (!pathEval.safe) continue;
      const approachPenaltyWeight = isPhysicsRunning ? 0.6 : 0;
      if (orientationMode === "free" && gripperForwardLocal) {
        const fkAttempt = computeKinematics(chain, attempt.jointValues);
        const eeSceneAttempt = fkAttempt.endEffector.position.clone().applyQuaternion(zToY);
        const approachDir = candidate.clone().sub(eeSceneAttempt);
        if (approachDir.lengthSq() > 1e-10) {
          approachDir.normalize();
          const gripperForwardScene = gripperForwardLocal
            .clone()
            .applyQuaternion(fkAttempt.endEffector.rotation)
            .applyQuaternion(zToY)
            .normalize();
          const alignment = gripperForwardScene.dot(approachDir);
          const score = alignment - pathEval.approachPenalty * approachPenaltyWeight;
          if (chosenScore === null || score > chosenScore) {
            chosenScore = score;
            pick = attempt;
          }
          continue;
        }
      }
      pick = attempt;
      chosenScore = Number.POSITIVE_INFINITY;
      break;
    }
    if (pick && chosenScore === Number.POSITIVE_INFINITY) break;
  }

  if (!pick) return false;

  const sceneActions = getSceneActions();
  const jointUpdates: Array<{ jointUuid: string; value: number }> = [];
  pick.jointValues.forEach((val, idx) => {
    const link = dofLinks.find((l) => l.dofIndex === idx) || null;
    const jointUuid = link?.jointUuid;
    if (jointUuid) jointUpdates.push({ jointUuid, value: val });
  });
  if (jointUpdates.length > 0) sceneActions.updateJointValues(jointUpdates);
  try {
    const w = globalThis as unknown as { __ikLastSolution?: number[] };
    w.__ikLastSolution = pick.jointValues;
  } catch {
    /* ignore */
  }
  return true;
}
